import React from "react";
import { Avatar, MenuItem, Select, TextField, Typography } from "@mui/material";
import styles from "../insuranceStatement.module.css";

const ADDITIONAL_MINUTES_OPTIONS = [0, 5, 10, 15, 20, 30, 45, 60];

function DropdownChevron({ className }) {
  return (
    <svg className={className} width="12" height="7" viewBox="0 0 12 7" fill="none">
      <path
        d="M1 1L6 6L11 1"
        stroke="#197F8F"
        strokeWidth="1.66667"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function NumberedTitle({ number, children, className }) {
  return (
    <Typography className={className} component="div" style={{ display: "flex" }}>
      <span style={{ width: 16, flexShrink: 0, textAlign: "right", marginRight: 4 }}>{number}.</span>
      <span>{children}</span>
    </Typography>
  );
}

function DaysUsedCheckIcon() {
  return (
    <svg width="21" height="21" viewBox="0 0 21 21" fill="none">
      <circle cx="10.5" cy="10.5" r="9.25" stroke="#0C8092" strokeWidth="1.6" />
      <path
        d="M5.7 11.3L9 14.5L15.3 6.8"
        stroke="#0C8092"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function StatementStep({
  patients,
  statementData = {},
  onUpdateField,
  activeId,
  onActiveChange,
  isFinalized,
  onEdit,
  hideSteps,
  complianceDays,
  showValidationError,
}) {
  const setActiveId = (id) => onActiveChange(id);

  const update = (field, value) => {
    onUpdateField(field, value);
  };

  const data = statementData;

  const activePatient = patients.find((p) => p.id === activeId) ?? patients[0];

  const isConditionInvalid = showValidationError && String(data.condition ?? "").trim() === "";
  const isMinutesInvalid = showValidationError && String(data.minutes ?? "").trim() === "";

  return (
    <div className={styles.layout}>
      {/* Left — patient list, plus (in individual view) the day-used/eligibility panel anchored below it */}
      <div className={styles.patientList}>
        <Typography className={styles.patientListTitle}>
          {hideSteps ? "Patient:" : `${patients.length} statements ready`}
        </Typography>

        <div className={`${styles.patientListInner} ${hideSteps ? styles.patientListInnerSpaced : ""}`}>
          {patients.map((patient) => (
            <div
              key={patient.id}
              onClick={() => setActiveId(patient.id)}
              className={`${styles.patientItem} ${
                hideSteps || patient.id === activeId ? styles.patientItemActive : ""
              }`}
            >
              <Avatar
                src={patient.avatarUrl || undefined}
                style={{
                  width: 28,
                  height: 28,
                  fontSize: 12,
                  backgroundColor: "#197f8f",
                  fontFamily: "Poppins",
                  flexShrink: 0,
                }}
              >
                {patient.name.charAt(0)}
              </Avatar>
              <span className={styles.patientName}>{patient.name}</span>
            </div>
          ))}

          {hideSteps && (
            <div className={styles.individualDayUsedBlock}>
              <div className={styles.individualDaysUsedRow}>
                {activePatient?.usedDays !== 0 && <DaysUsedCheckIcon />}
                <span className={styles.individualDaysUsedText}>
                  <span>Days used:</span>
                  <span>{activePatient?.daysUsed}</span>
                </span>
              </div>
              <div className={styles.individualEligibility}>
                <Typography className={styles.individualEligibilityTitle}>Eligibility rule</Typography>
                <Typography className={styles.individualEligibilityBody}>
                  Eligible if the device has been used for at least {complianceDays} days in the last{" "}
                  {activePatient?.windowDays ?? 30} days.
                </Typography>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Divider */}
      <div className={styles.divider} />

      {/* Right — statement content */}
      <div className={styles.statementPanel}>
        {isFinalized ? (
          <div className={styles.finalizedWrapper}>
            <div className={styles.statementViewHeader}>
              <button className={styles.editButton} onClick={onEdit}>
                Edit
              </button>
            </div>

            <div className={styles.finalizedBox}>
              <div className={styles.finalizedList}>
                <div className={styles.finalizedItem}>
                  <div>
                    <NumberedTitle number={"1"} className={styles.finalizedItemTitle}>
                      Initial enrollment
                    </NumberedTitle>
                    <Typography className={styles.textMuted}>
                      Can only bill once per patient, not a monthly charge
                    </Typography>
                  </div>
                  <Typography className={styles.finalizedText}>
                    Patient enrolled in remote patient monitoring for{" "}
                    <span className={styles.finalizedHighlight}>{data.condition}</span>; consent
                    obtained and RPM education completed (CPT 99453).
                  </Typography>
                </div>

                <div className={styles.finalizedItem}>
                  <NumberedTitle number={2} className={styles.finalizedItemTitle}>
                    30-day RPM billing
                  </NumberedTitle>
                  <Typography className={styles.finalizedText}>
                    Device supplied and transmitted readings received for the required days (CPT
                    99454). Clinical staff reviewed data and spent{" "}
                    <span className={styles.finalizedHighlight}>{data.minutes}</span> minutes
                    monitoring and coordinating care this month, including interactive
                    communication with the patient/caregiver (CPT 99457).
                  </Typography>
                </div>

                <div className={styles.finalizedItem}>
                  <NumberedTitle number={3} className={styles.finalizedItemTitle}>
                    Additional time spent
                  </NumberedTitle>
                  <Typography className={styles.finalizedText}>
                    Additional{" "}
                    <span className={styles.finalizedHighlight}>{data.additionalMinutes}</span>{" "}
                    minutes of clinical monitoring/care management time were spent this billing
                    period (CPT 99458). Clinical plan updated based on RPM findings.
                  </Typography>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Section 1 — Initial enrollment */}
            <div className={`${styles.section} ${styles.section1}`}>
              <div>
                <NumberedTitle number={1} className={styles.sectionTitle}>
                  Initial enrollment
                </NumberedTitle>
                <Typography className={styles.textMutedEdit}>
                  Can only bill once per patient, not a monthly charge
                </Typography>
              </div>

              <div className={styles.sectionBody}>
                <Typography className={styles.text}>
                  Patient enrolled in remote patient monitoring for
                </Typography>
                <div className={styles.conditionRow}>
                  <TextField
                    autoFocus
                    variant="outlined"
                    placeholder="Condition"
                    value={data.condition}
                    onChange={(e) => update("condition", e.target.value)}
                    style={{ flex: 1 }}
                    inputProps={{
                      maxLength: 200,
                      style: { fontFamily: "Poppins", fontSize: 16, color: "#444", padding: "12px 24px" },
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "6px",
                        backgroundColor: "#fff",
                        "& fieldset": {
                          borderColor: isConditionInvalid ? "#f75f56" : "#197f8f",
                          borderWidth: 1,
                        },
                        "&:hover fieldset": {
                          borderColor: isConditionInvalid ? "#f75f56" : "#197f8f",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: isConditionInvalid ? "#f75f56" : "#197f8f",
                          borderWidth: 2,
                        },
                      },
                    }}
                  />
                  <Typography className={`${styles.text} ${styles.semicolon}`}>;</Typography>
                </div>
                <Typography className={styles.text}>
                  consent obtained and RPM education completed (CPT 99453).
                </Typography>
              </div>
            </div>

            {/* Section 2 — 30-day RPM billing */}
            <div className={styles.section}>
              <NumberedTitle number={2} className={styles.sectionTitle}>
                30-day RPM billing
              </NumberedTitle>

              <div className={styles.sectionBody}>
                <Typography className={styles.text}>
                  Device supplied and transmitted readings received for the required days (CPT
                  99454). Clinical staff reviewed data and spent{" "}
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={4}
                    value={data.minutes}
                    onChange={(e) => update("minutes", e.target.value.replace(/[^0-9]/g, ""))}
                    className={styles.minutesInput}
                    style={isMinutesInvalid ? { borderColor: "#f75f56" } : undefined}
                  />{" "}
                  minutes monitoring and coordinating care this month, including interactive
                  communication with the patient/caregiver (CPT 99457).
                </Typography>
              </div>
            </div>

            {/* Section 3 — Additional time spent */}
            <div className={styles.section}>
              <NumberedTitle number={3} className={styles.sectionTitle}>
                Additional time spent
              </NumberedTitle>

              <div className={styles.sectionBody}>
                <Typography className={styles.text} component="div">
                  Additional{" "}
                  <Select
                    value={data.additionalMinutes}
                    onChange={(e) => update("additionalMinutes", Number(e.target.value))}
                    IconComponent={DropdownChevron}
                    variant="standard"
                    disableUnderline
                    sx={{
                      display: "inline-flex",
                      verticalAlign: "middle",
                      margin: "0 6px",
                      width: 66,
                      height: 33,
                      boxSizing: "border-box",
                      border: "1px solid #197f8f",
                      borderRadius: "8px",
                      backgroundColor: "#fff",
                      fontFamily: "Poppins",
                      fontSize: 16,
                      color: "#444",
                      "&.Mui-focused": {
                        borderWidth: 2,
                        boxShadow: "0px 1px 2px 0px rgba(16,24,40,0.05), 0px 0px 0px 4px rgba(25,127,143,0.25)",
                      },
                      "& .MuiSelect-select": {
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "0 !important",
                        paddingRight: "22px !important",
                        minHeight: "unset",
                      },
                      "& .MuiSelect-icon": {
                        right: "8px",
                        top: "13px",
                      },
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          width: 66,
                          maxHeight: 100,
                          overflowY: "auto",
                          marginTop: "8px",
                          padding: "4px",
                          backgroundColor: "#eff7f7",
                          border: "2px solid #197f8f",
                          borderRadius: "8px",
                          boxShadow:
                            "0px 12px 8px 0px rgba(16,24,40,0.08), 0px 4px 3px 0px rgba(16,24,40,0.03)",
                        },
                      },
                      MenuListProps: {
                        sx: {
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          padding: "4px 0",
                        },
                      },
                    }}
                  >
                    {ADDITIONAL_MINUTES_OPTIONS.map((minutes) => (
                      <MenuItem
                        key={minutes}
                        value={minutes}
                        sx={{
                          width: 66,
                          height: 33,
                          maxHeight: 33,
                          borderRadius: 0,
                          fontFamily: "Poppins",
                          fontSize: 16,
                          fontWeight: 400,
                          color: "#313131",
                          justifyContent: "center",
                          backgroundColor: "transparent",
                          "&.Mui-selected": {
                            backgroundColor: "#fff",
                            fontWeight: 500,
                            boxShadow: "0px 4px 2px 0px rgba(0,0,0,0.05)",
                          },
                          "&.Mui-selected:hover": {
                            backgroundColor: "#fff",
                          },
                        }}
                      >
                        {minutes}
                      </MenuItem>
                    ))}
                  </Select>{" "}
                  minutes of clinical monitoring/care management time were spent this billing
                  period (CPT 99458). Clinical plan updated based on RPM findings.
                </Typography>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
